import { useEffect, useRef, useState } from "react";
import { useBlocker, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Save, X, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { TemplateFieldList } from "./TemplateFieldList";
import { AddFieldDialog } from "./AddFieldDialog";
import { FieldEditorDialog } from "./FieldEditorDialog";
import { ExcelTemplateFileManager } from "./ExcelTemplateFileManager";
import { UnsavedChangesDialog } from "../audit/UnsavedChangesDialog";
import { useWorkPaperTemplateEditor } from "../../hooks/useWorkPaperTemplateEditor";
import {
  FeatureFlag,
  useFeatureFlag,
} from "../../contexts/launchDarklyContext";
import {
  DraftTemplateField,
  TemplateType,
  TemplateTypeEnum,
  getTemplateTypeLabel,
} from "../../models/workPaperTemplate";
import { LoadingSpinner } from "../ui/loadingSpinner/loadingSpinner";

// Why the template can't be saved yet, or null when it can.
function saveDisabledReasonFor(
  name: string,
  type: TemplateType | null,
): string | null {
  if (name.trim() === "") {
    return "Enter a template name before saving.";
  }
  if (type === null) {
    return "Choose a template type before saving.";
  }
  return null;
}

// A new template's type before the user picks one. Without Work Steps it's
// always control. Otherwise it's the kind a session's empty template picker
// linked with (?type=), or none, so the user chooses.
function newTemplateDefaultType(
  workStepsEnabled: boolean,
  requested: string | null,
): TemplateType | null {
  if (!workStepsEnabled) return "control";
  const parsed = TemplateTypeEnum.safeParse(requested);
  if (!parsed.success) return null;
  return parsed.data;
}

interface WorkPaperTemplateEditorProps {
  templateId?: string | null;
}

// Seeds a new template's type from the Work Steps flag once, on mount, so it
// must only be mounted once flags are authoritative (see
// WorkPaperTemplateEditorPage).
export function WorkPaperTemplateEditor({
  templateId,
}: WorkPaperTemplateEditorProps) {
  const navigate = useNavigate();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<DraftTemplateField | null>(
    null,
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // react-router registers the blocker predicate from an effect, so the
  // predicate judging a navigation is always the one from the last committed
  // render. save() navigates in its own async continuation, before React has
  // committed the state that clears the dirty flag, so that predicate would
  // block a successful save's own navigation. A ref is read live inside the
  // predicate, so gate on one instead, set only for the post-save navigate.
  const skipNavigationBlockRef = useRef(false);

  // Work step templates exist only where Work Steps is on; elsewhere every
  // template is a control template and the type is never shown.
  const workStepsEnabled = useFeatureFlag(FeatureFlag.workStepsIntegration);
  const [searchParams] = useSearchParams();

  const {
    loading,
    saving,
    error,
    draftTemplate,
    hasUnsavedChanges,
    isReadOnly,
    updateName,
    updateType,
    addField,
    updateField,
    deleteField,
    reorderFields,
    save,
    reset,
    clearError,
  } = useWorkPaperTemplateEditor({
    templateId,
    defaultType: newTemplateDefaultType(
      workStepsEnabled,
      searchParams.get("type"),
    ),
    onSaveSuccess: (id) => {
      skipNavigationBlockRef.current = true;
      // A save started from the unsaved changes dialog already has a
      // navigation waiting on blocker.proceed(). Navigating here as well
      // races it, and for a new template the user can land back in the
      // editor instead of where they were going. Read through a ref: this
      // callback was created before the save, and the blocker may have
      // blocked a navigation while it ran.
      if (blockerStateRef.current === "blocked") {
        return;
      }
      navigate(`/audit/work-paper-templates/${id}/edit`);
    },
  });

  // Clear the suppression once the save settles. onSaveSuccess navigates from
  // inside the save, and that navigation is judged before setSaving(false)
  // commits, so this only ever runs after the ref has done its job.
  useEffect(() => {
    if (!saving) {
      skipNavigationBlockRef.current = false;
    }
  }, [saving]);

  const handleCancel = () => {
    navigate("/audit/work-paper-templates");
  };

  // Warn before a full page unload (refresh / tab close) with unsaved changes.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !isReadOnly) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, isReadOnly]);

  // Block in-app navigation (Cancel, links, back/forward) when there are
  // unsaved changes so we can offer to save, discard, or stay via the dialog.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges &&
      !isReadOnly &&
      !skipNavigationBlockRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );
  const blockerStateRef = useRef(blocker.state);
  blockerStateRef.current = blocker.state;

  const handleEditField = (fieldId: string) => {
    const field = draftTemplate.fields.find((f) => f.id === fieldId);
    if (field) {
      setEditingField(field);
      setEditDialogOpen(true);
    }
  };

  const hasFields = draftTemplate.fields.some((f) => !f.isDeleted);
  // A template's type is chosen when it's created and never changes after.
  const isNewTemplate = draftTemplate.id === null;

  const saveDisabledReason = saveDisabledReasonFor(
    draftTemplate.name,
    draftTemplate.type,
  );

  const saveButton = (
    <Button
      onClick={save}
      disabled={
        saving ||
        !hasUnsavedChanges ||
        isReadOnly ||
        saveDisabledReason !== null
      }
    >
      <Save className="h-4 w-4 mr-2" />
      {saving ? "Saving..." : "Save"}
    </Button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {templateId ? "Edit Template" : "Create Template"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure fields for your workpaper template
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={saving}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          {!isReadOnly && saveDisabledReason !== null ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span: disabled buttons swallow pointer events, so the
                    trigger needs an enabled wrapper */}
                <span tabIndex={0} className="inline-flex">
                  {saveButton}
                </span>
              </TooltipTrigger>
              <TooltipContent>{saveDisabledReason}</TooltipContent>
            </Tooltip>
          ) : (
            saveButton
          )}
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={clearError}
              className="ml-4 hover:opacity-70"
              aria-label="Dismiss error"
            >
              <X className="h-4 w-4" />
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Read-only warning */}
      {isReadOnly && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This is a base template and cannot be edited. To make changes,
            please duplicate this template first.
          </AlertDescription>
        </Alert>
      )}

      {/* Template Name and Type */}
      <div className="flex items-start gap-4">
        <div className="space-y-2 max-w-xl w-full">
          <Label htmlFor="templateName">Template Name</Label>
          <Input
            id="templateName"
            value={draftTemplate.name}
            onChange={(e) => updateName(e.target.value)}
            placeholder="e.g., SOX IT Controls Template"
            disabled={isReadOnly}
          />
        </div>
        {workStepsEnabled && isNewTemplate && (
          <div className="space-y-2 w-56">
            <Label htmlFor="templateType">Template Type</Label>
            <Select
              value={draftTemplate.type ?? ""}
              onValueChange={(value) =>
                updateType(TemplateTypeEnum.parse(value))
              }
              disabled={isReadOnly || hasFields}
            >
              <SelectTrigger
                id="templateType"
                data-testid="template-editor-type-select"
                aria-describedby={
                  hasFields ? "templateTypeLockedReason" : undefined
                }
              >
                <SelectValue placeholder="Select a type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  value="control"
                  data-testid="template-editor-type-option-control"
                >
                  {getTemplateTypeLabel("control")}
                </SelectItem>
                <SelectItem
                  value="work_step"
                  data-testid="template-editor-type-option-work_step"
                >
                  {getTemplateTypeLabel("work_step")}
                </SelectItem>
              </SelectContent>
            </Select>
            {/* The type decides which field sources are valid, so a field
                added for one type would be refused under the other. */}
            {hasFields && (
              <p
                id="templateTypeLockedReason"
                className="text-sm text-muted-foreground"
              >
                Remove the fields to change the template type.
              </p>
            )}
          </div>
        )}
        {workStepsEnabled && !isNewTemplate && (
          <div className="space-y-2 w-56">
            <p className="text-sm font-medium leading-none">Template Type</p>
            <p
              className="flex h-10 items-center text-sm"
              data-testid="template-editor-template-type"
            >
              {draftTemplate.type !== null &&
                getTemplateTypeLabel(draftTemplate.type)}
            </p>
            <p className="text-sm text-muted-foreground">
              Set when the template was created.
            </p>
          </div>
        )}
      </div>

      {/* Excel Template File Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Excel Template File</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Upload an Excel file that will be used as the base template for work
            papers
          </p>
        </div>
        <ExcelTemplateFileManager
          templateId={draftTemplate.id}
          isReadOnly={isReadOnly}
        />
      </div>

      {/* Fields Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Fields</h3>
            <p className="text-sm text-muted-foreground">
              {draftTemplate.fields.filter((f) => !f.isDeleted).length} field(s)
              configured
            </p>
          </div>
          {!isReadOnly && draftTemplate.type === null && (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span: disabled buttons swallow pointer events, so the
                    trigger needs an enabled wrapper */}
                <span tabIndex={0} className="inline-flex">
                  <Button
                    disabled
                    data-testid="template-editor-add-field-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Field
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Choose a template type first.</TooltipContent>
            </Tooltip>
          )}
          {!isReadOnly && draftTemplate.type !== null && (
            <Button
              onClick={() => setAddDialogOpen(true)}
              data-testid="template-editor-add-field-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          )}
        </div>

        <TemplateFieldList
          fields={draftTemplate.fields}
          onEdit={handleEditField}
          onDelete={deleteField}
          onReorder={reorderFields}
          readOnly={isReadOnly}
        />
      </div>

      {/* Unsaved changes indicator */}
      {hasUnsavedChanges && !isReadOnly && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 border border-yellow-400 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-yellow-900">
            You have unsaved changes
          </p>
        </div>
      )}

      {/* Dialogs */}
      {/* Keyed on the type so switching it remounts the dialog: its static
          field state is seeded from the type and would otherwise keep the
          previous type's source. */}
      {draftTemplate.type !== null && (
        <AddFieldDialog
          key={draftTemplate.type}
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          onAdd={addField}
          templateType={draftTemplate.type}
        />
      )}

      {draftTemplate.type !== null && (
        <FieldEditorDialog
          field={editingField}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onUpdate={updateField}
          readOnly={isReadOnly}
          templateType={draftTemplate.type}
        />
      )}

      {/* Unsaved changes prompt when navigating away */}
      <UnsavedChangesDialog
        open={blocker.state === "blocked"}
        entityLabel="template"
        isSaving={saving}
        saveDisabledReason={saveDisabledReason}
        onSave={async () => {
          const success = await save();
          if (success && blocker.state === "blocked") {
            blocker.proceed();
          }
        }}
        onLeave={() => {
          if (blocker.state === "blocked") {
            reset();
            blocker.proceed();
          }
        }}
        onCancel={() => {
          if (blocker.state === "blocked") {
            blocker.reset();
          }
        }}
      />
    </div>
  );
}
